import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../ui/ConfirmDialog';
import Dialog from '../ui/Dialog';
import { formatNumber } from '../../utils/format';

/**
 * P-02a/C1 — a task with logged hours is never deleted (the server answers
 * 409 `TASK_HAS_WORK` with the minutes). Its minutes when `err` is that
 * refusal, otherwise null (any other error keeps its usual handling).
 */
export function taskHasWorkMinutes(err: unknown): number | null {
  const e = err as { error?: { error_code?: string; details?: { minutes?: unknown } } } | null;
  if (e?.error?.error_code !== 'TASK_HAS_WORK') return null;
  return Number(e.error.details?.minutes) || 0;
}

/**
 * The refusal as a dialog: the message and two buttons — [ביטול המשימה], which
 * opens the existing cancel action (with a reason), and [סגירה].
 */
export default function TaskHasWorkDialog({ minutes, onCancelTask, onClose }: {
  minutes: number;
  onCancelTask: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <ConfirmDialog
      open
      title={t('tasks.hasWork.title')}
      description={t('tasks.hasWork.message', { hours: formatNumber(minutes / 60, 1) })}
      confirmLabel={t('ticketDetail.cancelTask')}
      cancelLabel={t('common.close')}
      onConfirm={onCancelTask}
      onCancel={onClose}
    />
  );
}

/**
 * P-02c/F-04 (D-25) — the same refusal for a service ticket (409
 * `TICKET_HAS_WORK`). Its minutes when `err` is that refusal, otherwise null.
 */
export function ticketHasWorkMinutes(err: unknown): number | null {
  const e = err as { error?: { error_code?: string; details?: { minutes?: unknown } } } | null;
  if (e?.error?.error_code !== 'TICKET_HAS_WORK') return null;
  return Number(e.error.details?.minutes) || 0;
}

/**
 * The service-ticket refusal: the message, [סגירה], and — when the ticket can
 * still be closed — [סגירת הקריאה], which runs the existing close action.
 */
export function TicketHasWorkDialog({ minutes, onCloseTicket, onClose }: {
  minutes: number;
  onCloseTicket?: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const title = t('tickets.hasWork.title');
  const description = t('tickets.hasWork.message', { hours: formatNumber(minutes / 60, 1) });
  return onCloseTicket ? (
    <ConfirmDialog
      open
      title={title}
      description={description}
      confirmLabel={t('ticketDetail.closeTicket')}
      cancelLabel={t('common.close')}
      onConfirm={onCloseTicket}
      onCancel={onClose}
    />
  ) : (
    <Dialog
      open
      onClose={onClose}
      role="alertdialog"
      title={title}
      description={description}
      size="sm"
      hideCloseButton
      detent="medium"
      bodyClassName="hidden"
      footer={(
        <div className="flex w-full items-center justify-end gap-3">
          <button type="button" className="btn btn-secondary flex-1 sm:flex-none" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      )}
    >
      {null}
    </Dialog>
  );
}
